import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, shareReplay } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { ComparatorUtils } from 'app/utils/comparator.utils';
import { ANYTHING, CachedValue, CacheScenario } from 'app/models/api/cache';
import { SCENARIOS } from './cache.scenarios';
import { ObjectUtils } from '@festo-ax/smartenance-shared-ui';

/**
 * IMPORTANT NOTE
 * For current implementation request to cache can be only without body
 */
@Injectable()
export class ApiCacheInterceptor implements HttpInterceptor {
  private scenarios: CacheScenario[] = inject(SCENARIOS);
  private cache: Map<string, CachedValue> = new Map<string, CachedValue>();
  private pendingRequests: Map<string, Observable<HttpEvent<unknown>>> = new Map<string, Observable<HttpEvent<unknown>>>();

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const url: string = request.url;
    const params: HttpParams = request.params;
    const method: string = request.method;

    // clear cache if we get cleaning cache request
    this.scenarios.forEach((sc: CacheScenario) => {
      // try to find scenarios that must be cleaned by current request
      for (const cleaningEndpoint of sc.cleaningEndpoints) {
        if (ComparatorUtils.areUrlsEqual(cleaningEndpoint.url, url, [ANYTHING]) && cleaningEndpoint.method === method) {
          const urlToCleanMask: string = sc.endpoint.url;
          const methodOfUrlToClean: string = sc.endpoint.method;
          const cleanKeyMask: string = this.createKeyToCache(urlToCleanMask, methodOfUrlToClean);

          this.cache.forEach((_: CachedValue, key: string, cache: Map<string, CachedValue>) => {
            const baseKey = this.extractBaseUrl(key);

            if (ComparatorUtils.areUrlsEqual(cleanKeyMask, baseKey, [ANYTHING])) {
              // if we found corresponding cached requests, we remove pending requests values as well
              cache.delete(key);
              this.pendingRequests.delete(key);
            }
          });
          // no need to check further
          break;
        }
      }
    });

    // search for caching scenario for each request
    const cachingScenario: CacheScenario | undefined = this.scenarios.find((cs: CacheScenario) =>
      ComparatorUtils.areUrlsEqual(cs.endpoint.url, url, [ANYTHING]) && cs.endpoint.method === method
    );

    if (cachingScenario) {
      const keyToCache: string = this.createKeyToCache(url, cachingScenario.endpoint.method, params);
      const cachedValue: CachedValue | undefined = this.cache.get(keyToCache);

      // if response is found in the cache, and it is not expired return it
      if (cachedValue && cachedValue.expiredAt > Date.now()) {
        const body: unknown = ObjectUtils.deepClone(cachedValue.storedBody);
        // to avoid mutation of body in request we provide deep clone of body and provide this value on cached request
        // also in some case we cache 204 no content, so we prevent an error by checking if body is object
        if (ObjectUtils.isObject(body)) {
          Object.assign(cachedValue.response.body, body);
        }
        return of(cachedValue.response);
      }

      // Check if there is already a pending request for this cache key
      const pendingRequest: Observable<HttpEvent<unknown>> = this.pendingRequests.get(keyToCache);
      if (pendingRequest) {
        // Return the pending request as observable, ensuring the second request waits
        return pendingRequest;
      }

      // If there is no pending request, create one and store it in the pendingRequests map
      const requestObservable: Observable<HttpEvent<unknown>> = next.handle(request).pipe(
        tap((response: HttpEvent<unknown>) => {
          if (response instanceof HttpResponse) {
            // Cache the response when it arrives, for response like 204 no content we store just primitive value
            const storedBody: unknown = ObjectUtils.isObject(response.body)
              ? Object.assign({}, ObjectUtils.deepClone(response.body))
              : response.body;

            const expiredAt: number = Date.now() + cachingScenario.cacheTime;
            this.cache.set(keyToCache, { response, expiredAt, storedBody });
          }
        }),
        finalize(() => {
          // Once the request completes, remove it from pendingRequests
          this.pendingRequests.delete(keyToCache);
        }),
        shareReplay(1) // Share the response, and replay it to all subscribers
      );

      // Store the pending request observable in the pendingRequests map
      this.pendingRequests.set(keyToCache, requestObservable);

      return requestObservable;
    }

    return next.handle(request); // If no caching scenario, proceed with the request as usual
  }

  private createKeyToCache(url: string, method: string, params: HttpParams | null = null): string {
    if (params && params.toString()) {
      return `${method}${url}?${params.toString()}`;
    }

    return `${method}${url}`;
  }

  private extractBaseUrl(url: string): string {
    const index = url.indexOf('?');
    return index === -1 ? url : url.substring(0, index);
  }
}

